import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box, Typography, List, ListItem,
    CircularProgress, Alert, Link, Stack, Paper, Divider,
    TableContainer, Table, TableHead, TableRow, TableCell, TableBody,
    Accordion, AccordionSummary, AccordionDetails, Button, Chip, Tooltip
} from '@mui/material';
import {
    Hotel as HotelIcon,
    ExpandMore as ExpandMoreIcon,
    Gavel as GavelIcon,
    LocationOn as LocationOnIcon,
    Schedule as ScheduleIcon,
    OpenInNew as OpenInNewIcon,
    Directions as DirectionsIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import LinksTable, { LinkItem } from '../LinksTable';
import { fetchTournamentData, getCachedTournamentData } from '../../utils/tournamentScraper';
import { formatLocation, getLiveBarnLink } from '../../utils/eventUtils';
import { getRink, RINK_DATA, Rink } from '../../utils/locationUtils';
import { MhrRating, TournamentGame, TournamentPool } from '../../types';
import { MHR_RATINGS_2026_PW_MAJOR, getMhrRating } from '../../mhr-ratings';
import { logEvent } from '../../analytics';
import { useRoster } from '../../RosterContext';
import AdvancementPredictionsCard from './AdvancementPredictionsCard';

const howiesLinks2026: LinkItem[] = [
    { label: 'Website', url: 'https://200x85.com/howies-hometown-cup/', displayText: "CCM Howies Hometown Cup" },
    { label: 'Jets Schedule', url: 'https://my.200x85.com/tournaments/b242def3-6e03-4dba-8a7f-6b5960f35df0/teams/94e4431b-9173-4d1a-a686-7a4fb8ee13cb', displayText: "Jets Schedule (my.200x85.com)" },
    { label: 'Overall Standings', url: 'https://my.200x85.com/tournaments/b242def3-6e03-4dba-8a7f-6b5960f35df0/standings?division=895', displayText: 'Division Standings (12U: 2014)' },
    { label: 'Tournament Rules', url: 'https://200x85.com/howies-hometown-cup/?event=Rules', displayText: 'Official Rules & Tiebreakers' },
    { label: 'Facilities', url: 'https://200x85.com/howies-hometown-cup/?event=Facilities', displayText: 'Rink Locations & Maps' },
    { label: 'Hotel', url: 'https://www.hyatt.com/hyatt-place/en-US/grrzd-hyatt-place-grand-rapids-downtown', displayText: 'Hyatt Place Grand Rapids Downtown' },
];

/**
 * Formats game date & time string to explicitly indicate Eastern Time (EDT).
 */
export const formatEasternGameTime = (dateTimeStr: string): string => {
    if (!dateTimeStr) return '';
    const clean = dateTimeStr.replace(/\s+/g, ' ').trim();
    if (clean.includes('EDT') || clean.includes('EST') || clean.includes('ET')) {
        return clean;
    }
    return `${clean} EDT`;
};

const TournamentGameDisplay: React.FC<{ game: TournamentGame, ratings: Record<string, MhrRating> }> = ({ game, ratings }) => {
    const opponentTeam = game.homeTeam.includes('Jets') ? game.visitorTeam : game.homeTeam;
    const rink = getRink(game.location);
    const rinkData = rink !== Rink.Unknown ? RINK_DATA.get(rink) : null;

    const renderTeamWithRating = (teamName: string) => {
        const ratingInfo = getMhrRating(teamName, ratings);
        const isJets = teamName.includes('Jets');
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flexWrap: 'wrap' }}>
                <Typography
                    variant="body1"
                    sx={{
                        fontWeight: isJets ? 700 : 500,
                        color: isJets ? 'primary.main' : 'text.primary',
                        fontSize: { xs: '0.9rem', sm: '1rem' }
                    }}
                >
                    {teamName}
                </Typography>
                {ratingInfo && (
                    <Link
                        href={ratingInfo.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => logEvent('click_mhr_link', { team_name: teamName })}
                        sx={{ textDecoration: 'none', ml: 1, flexShrink: 0 }}
                    >
                        <Typography variant="caption" color="text.secondary">
                            ({ratingInfo.rating.toFixed(1)})
                        </Typography>
                    </Link>
                )}
            </Box>
        );
    };

    return (
        <ListItem sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 1.5, sm: 2.5 } }}>
            <Box sx={{ width: '100%' }}>
                {/* Header Bar: DateTime on Left, Status Badge / Score on Right */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                        <ScheduleIcon sx={{ fontSize: '1rem', color: 'primary.main', flexShrink: 0 }} />
                        <Typography variant="body2" fontWeight="bold" color="text.primary" sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
                            {formatEasternGameTime(game.dateTime)}
                        </Typography>
                    </Stack>
                    <Box sx={{ flexShrink: 0 }}>
                        {game.homeScore !== '-' || game.visitorScore !== '-' ? (
                            <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
                                {game.homeScore} - {game.visitorScore}
                            </Typography>
                        ) : (
                            <Chip
                                label={game.status || 'SCHEDULED'}
                                size="small"
                                variant="outlined"
                                color="primary"
                                sx={{ height: 22, fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 700 }}
                            />
                        )}
                    </Box>
                </Stack>

                {/* Team Matchup Box (Full Horizontal Width) */}
                <Paper variant="outlined" sx={{ p: { xs: 1, sm: 1.25 }, mb: 1, bgcolor: 'grey.50', borderRadius: 1.5 }}>
                    {/* HOME Team */}
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                        <Chip
                            label="HOME"
                            size="small"
                            sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: 'grey.200', color: 'text.secondary', flexShrink: 0 }}
                        />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            {renderTeamWithRating(game.homeTeam)}
                        </Box>
                    </Stack>
                    {/* AWAY Team */}
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                            label="AWAY"
                            size="small"
                            sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: 'grey.200', color: 'text.secondary', flexShrink: 0 }}
                        />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            {renderTeamWithRating(game.visitorTeam)}
                        </Box>
                    </Stack>
                </Paper>

                {/* Rink Details & Action Links */}
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={0.5} sx={{ pl: 0.5 }}>
                    <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 500 }}>
                            {formatLocation(game.location)}{rinkData?.address ? ` • ${rinkData.address}` : ''}
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: { xs: 0.5, sm: 0 } }}>
                        {game.boxscoreUrl && (
                            <Link
                                href={game.boxscoreUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="caption"
                                fontWeight="600"
                                onClick={() => logEvent('click_tournament_boxscore', { game_id: game.id, opponent: opponentTeam })}
                            >
                                Box Score
                            </Link>
                        )}
                        {getLiveBarnLink(game.location, game.dateTime)}
                    </Stack>
                </Stack>
            </Box>
        </ListItem>
    );
};

const TournamentStandingsTable: React.FC<{ pool: TournamentPool, ratings: Record<string, MhrRating> }> = ({ pool, ratings }) => {
    return (
        <Paper variant="outlined" elevation={0} sx={{ mb: { xs: 2.5, sm: 4 }, borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ p: { xs: 1.5, sm: 2 }, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.100', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { sm: 'center' }, gap: 0.75 }}>
                <Box>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>{pool.poolName}</Typography>
                    <Typography variant="caption" color="text.secondary">All 18 teams seeded #1–#18 based on 3 preliminary games</Typography>
                </Box>
                <Chip label="12U: 2014 Division" size="small" variant="outlined" sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }} />
            </Box>
            <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Team</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>GP</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>W</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>T</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>L</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>PTS</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>GF</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>GA</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>GD</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>QUOT</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>SEED</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pool.standings.map((team) => {
                            const isJets = team.teamName.includes('Jets');
                            const ratingInfo = getMhrRating(team.teamName, ratings);
                            return (
                                <TableRow
                                    key={team.teamName}
                                    sx={{
                                        bgcolor: isJets ? 'primary.50' : 'inherit',
                                        '&:hover': { bgcolor: isJets ? 'primary.100' : 'action.hover' }
                                    }}
                                >
                                    <TableCell component="th" scope="row">
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            {team.teamUrl ? (
                                                <Link
                                                    href={team.teamUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={() => logEvent('click_tournament_team_link', { team_name: team.teamName })}
                                                    color="inherit"
                                                    underline="hover"
                                                    sx={{ fontWeight: isJets ? 700 : 400 }}
                                                >
                                                    <Typography variant="body2">{team.teamName}</Typography>
                                                </Link>
                                            ) : (
                                                <Typography variant="body2" sx={{ fontWeight: isJets ? 700 : 400 }}>{team.teamName}</Typography>
                                            )}
                                            {ratingInfo && (
                                                <Link
                                                    href={ratingInfo.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={() => logEvent('click_mhr_link', { team_name: team.teamName })}
                                                    sx={{ textDecoration: 'none', ml: 1 }}
                                                >
                                                    <Typography variant="caption" color="text.secondary">
                                                        ({ratingInfo.rating.toFixed(1)})
                                                    </Typography>
                                                </Link>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">{team.gp}</TableCell>
                                    <TableCell align="right">{team.w}</TableCell>
                                    <TableCell align="right">{team.t}</TableCell>
                                    <TableCell align="right">{team.l}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{team.pts}</TableCell>
                                    <TableCell align="right">{team.gf}</TableCell>
                                    <TableCell align="right">{team.ga}</TableCell>
                                    <TableCell align="right">{team.gd}</TableCell>
                                    <TableCell align="right">{team.quot}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{team.seed}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};

const formatTimeAgo = (isoString?: string | null): string => {
    if (!isoString) return '';
    try {
        const date = new Date(isoString);
        const now = new Date();
        const diffSec = Math.floor((now.getTime() - date.getTime()) / 1000);
        if (diffSec < 45) return 'just now';
        if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
        if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    } catch {
        return '';
    }
};

const HowiesHometownCup2026Page: React.FC = () => {
    const { isAuthReady } = useRoster();
    const cached = useMemo(() => getCachedTournamentData('howies-hometown-cup-2026'), []);
    const [games, setGames] = useState<TournamentGame[]>(cached?.games || []);
    const [standings, setStandings] = useState<TournamentPool[]>(cached?.pools || []);
    const [lastUpdated, setLastUpdated] = useState<string | null>(cached?.updatedAt || null);
    const [isStale, setIsStale] = useState<boolean>(cached?.stale || false);
    const [loading, setLoading] = useState(!cached || (cached.games.length === 0 && cached.pools.length === 0));
    const [isCheckingUpdates, setIsCheckingUpdates] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadData = useCallback(async (force = false) => {
        if (!games.length && !standings.length) {
            setLoading(true);
        } else {
            setIsCheckingUpdates(true);
        }
        setError(null);

        try {
            const data = await fetchTournamentData('howies-hometown-cup-2026', { force });
            setGames(data.games || []);
            setStandings(data.pools || []);
            setLastUpdated(data.updatedAt || new Date().toISOString());
            setIsStale(data.stale || false);
        } catch (e: any) {
            console.error('Error fetching tournament data:', e);
            if (!games.length && !standings.length) {
                setError(e.message || 'Could not retrieve tournament schedule and standings.');
            }
        } finally {
            setLoading(false);
            setIsCheckingUpdates(false);
        }
    }, [games.length, standings.length]);

    useEffect(() => {
        if (!isAuthReady) return;
        loadData(false);
    }, [isAuthReady, loadData]);

    return (
        <>
            {/* Live Updates & Status Toolbar */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1, px: 0.5 }}>
                <Box>
                    {isCheckingUpdates ? (
                        <Chip
                            size="small"
                            icon={<CircularProgress size={12} color="inherit" />}
                            label="Checking for live updates..."
                            variant="outlined"
                            color="primary"
                            sx={{ fontWeight: 500 }}
                        />
                    ) : lastUpdated ? (
                        <Typography variant="caption" color="text.secondary">
                            {isStale ? 'Showing offline data • ' : ''}Last updated {formatTimeAgo(lastUpdated)}
                        </Typography>
                    ) : null}
                </Box>
                <Tooltip title="Check for latest tournament scores and schedule updates">
                    <span>
                        <Button
                            size="small"
                            variant="text"
                            startIcon={isCheckingUpdates ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon fontSize="small" />}
                            onClick={() => loadData(true)}
                            disabled={isCheckingUpdates}
                            sx={{ textTransform: 'none', fontSize: '0.8rem', color: 'text.secondary' }}
                        >
                            Refresh
                        </Button>
                    </span>
                </Tooltip>
            </Stack>

            {/* Team Hotel Card */}
            <Paper
                variant="outlined"
                elevation={0}
                sx={{
                    mb: { xs: 2, sm: 3 },
                    mt: { xs: 0.5, sm: 1 },
                    p: { xs: 1.5, sm: 2.5 },
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    borderColor: 'primary.light',
                    borderLeftWidth: 6,
                    borderLeftColor: 'primary.main'
                }}
            >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.5, sm: 2 }} justifyContent="space-between" alignItems={{ sm: 'center' }}>
                    <Box>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                            <HotelIcon color="primary" />
                            <Typography variant="h6" component="h3" fontWeight="bold" sx={{ fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
                                Team Hotel: Hyatt Place Grand Rapids Downtown
                            </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            140 Ottawa Avenue NW, Grand Rapids, MI, US 49503
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1.5} sx={{ mt: { xs: 1, sm: 0 }, flexShrink: 0 }}>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<OpenInNewIcon />}
                            href="https://www.hyatt.com/hyatt-place/en-US/grrzd-hyatt-place-grand-rapids-downtown"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Hotel Details
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<DirectionsIcon />}
                            href="https://www.google.com/maps/search/?api=1&query=140+Ottawa+Avenue+NW%2C+Grand+Rapids%2C+MI+49503"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Directions
                        </Button>
                    </Stack>
                </Stack>
            </Paper>

            {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}
            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            {!loading && !error && (
                <>
                    {/* Schedule & Results */}
                    <Paper variant="outlined" elevation={0} sx={{ mb: { xs: 2.5, sm: 4 }, borderRadius: 2, overflow: 'hidden' }}>
                        <Box sx={{ p: { xs: 1.5, sm: 2 }, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.100' }}>
                            <Typography variant="h6" sx={{ fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>Schedule & Results</Typography>
                        </Box>
                        {games.length > 0 ? (
                            <List disablePadding>
                                {games.map((game, index) => (
                                    <React.Fragment key={game.id}>
                                        {index > 0 && <Divider component="li" />}
                                        <TournamentGameDisplay game={game} ratings={MHR_RATINGS_2026_PW_MAJOR} />
                                    </React.Fragment>
                                ))}
                            </List>
                        ) : (
                            <Typography sx={{ p: 2 }}>Schedule not yet available.</Typography>
                        )}
                    </Paper>

                    {/* Overall Standings */}
                    {standings.map(pool => (
                        <TournamentStandingsTable key={pool.poolName} pool={pool} ratings={MHR_RATINGS_2026_PW_MAJOR} />
                    ))}

                    {/* Live Advancement Predictions & Playoff Outlook Card */}
                    <AdvancementPredictionsCard games={games} standings={standings} />

                    {/* Tournament Format & Advancement Breakdown Card */}
                    <Paper
                        variant="outlined"
                        elevation={0}
                        sx={{
                            mb: { xs: 2.5, sm: 4 },
                            p: { xs: 1.5, sm: 3 },
                            borderRadius: 2,
                            bgcolor: 'background.paper',
                            borderColor: 'divider',
                            borderLeftWidth: 6,
                            borderLeftColor: 'secondary.main'
                        }}
                    >
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                            <GavelIcon color="secondary" />
                            <Typography variant="h6" component="h3" fontWeight="bold" sx={{ fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
                                How Advancement & Playoffs Work (18-Team Single Pool Format)
                            </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Unlike multi-pool tournaments where top pool seeds automatically cross over, all 18 teams compete in a single division table (Pool A). Here is how seeding and advancement progress across the weekend:
                        </Typography>

                        <Stack spacing={{ xs: 1.5, sm: 2 }}>
                            {/* Phase 1 */}
                            <Paper variant="outlined" sx={{ p: { xs: 1.25, sm: 2 }, bgcolor: 'grey.50', borderRadius: 1.5 }}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 0.5 }}>
                                    <Chip label="Phase 1" size="small" color="primary" sx={{ fontWeight: 'bold', width: 'fit-content' }} />
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        Preliminary Round Robin (Friday – Saturday Midday • Games #1 – #27)
                                    </Typography>
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                    • Each team plays <strong>3 preliminary games</strong>.<br />
                                    • Teams earn 3 pts for a regulation win, 2 pts for a shootout win, 1 pt for a shootout loss, and 0 pts for a regulation loss.<br />
                                    • Following the conclusion of Game #27 on Saturday afternoon (~2:30 PM EDT), all 18 teams are seeded <strong>#1 through #18</strong> on the overall standings board using tournament tiebreakers.
                                </Typography>
                            </Paper>

                            {/* Phase 2 */}
                            <Paper variant="outlined" sx={{ p: { xs: 1.25, sm: 2 }, bgcolor: 'grey.50', borderRadius: 1.5 }}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 0.5 }}>
                                    <Chip label="Phase 2" size="small" color="secondary" sx={{ fontWeight: 'bold', width: 'fit-content' }} />
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        Saturday Evening Crossover & Consolation Round (Saturday 5:00 PM – 8:35 PM • Games #28 – #36)
                                    </Typography>
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                    • <strong>Guaranteed 4th Game:</strong> There are 9 games scheduled across Saturday evening so that <strong>every single team</strong> plays a 4th game.<br />
                                    • <strong>Playoff Qualifiers:</strong> Top-seeded teams square off in playoff qualifier / quarterfinal matchups to earn the 4 Semifinal spots on Sunday morning.<br />
                                    • <strong>Consolation Matchups:</strong> Remaining seeds play paired consolation games.<br />
                                    • <em>Opponents and rink assignments for Saturday evening are posted by tournament officials once Saturday midday games finish.</em>
                                </Typography>
                            </Paper>

                            {/* Phase 3 */}
                            <Paper variant="outlined" sx={{ p: { xs: 1.25, sm: 2 }, bgcolor: 'grey.50', borderRadius: 1.5 }}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 0.5 }}>
                                    <Chip label="Phase 3" size="small" color="success" sx={{ fontWeight: 'bold', width: 'fit-content' }} />
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        Sunday Championship Bracket (Sunday Morning & Afternoon • Games #37 – #39)
                                    </Typography>
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                    • <strong>Semifinals (Sunday Morning at Eagles Ice Center):</strong><br />
                                    &nbsp;&nbsp;– Game #37 at <strong>07:45 AM EDT</strong><br />
                                    &nbsp;&nbsp;– Game #38 at <strong>09:10 AM EDT</strong><br />
                                    • <strong>Championship Final (Sunday Afternoon at Eagles Ice Center):</strong><br />
                                    &nbsp;&nbsp;– Game #39 at <strong>02:25 PM EDT</strong> between the two Semifinal winners.
                                </Typography>
                            </Paper>
                        </Stack>
                    </Paper>

                    {/* Tiebreaker Rules Summary Accordion */}
                    <Accordion variant="outlined" sx={{ mb: { xs: 2, sm: 3 }, borderRadius: 2, '&:before': { display: 'none' } }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <GavelIcon color="primary" />
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Official Tiebreaker Rules & Gameplay Guidelines
                                </Typography>
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                            <Divider sx={{ mb: 2 }} />

                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                🏆 Point System:
                            </Typography>
                            <Box component="ul" sx={{ pl: 3, mb: 2, mt: 0.5 }}>
                                <li><Typography variant="body2"><strong>3 Points:</strong> Regulation Win</Typography></li>
                                <li><Typography variant="body2"><strong>2 Points:</strong> Shootout Win</Typography></li>
                                <li><Typography variant="body2"><strong>1 Point:</strong> Shootout Loss</Typography></li>
                                <li><Typography variant="body2"><strong>0 Points:</strong> Regulation Loss</Typography></li>
                            </Box>

                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                🎯 Overtime & Shootout Rules:
                            </Typography>
                            <Box component="ul" sx={{ pl: 3, mb: 2, mt: 0.5 }}>
                                <li>
                                    <Typography variant="body2">
                                        <strong>Preliminary Games:</strong> No overtime. If tied at regulation end, immediately proceeds to a 3-player shootout, then sudden death shootout. (Every eligible player on the bench must shoot before any player shoots a second time).
                                    </Typography>
                                </li>
                                <li>
                                    <Typography variant="body2">
                                        <strong>Playoff / Elimination Games:</strong> Goaltenders switch ends; one 3-minute period of 3-on-3 sudden-death play; followed by 3-player shootout and sudden death shootout.
                                    </Typography>
                                </li>
                            </Box>

                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                📊 Team Seeding Tiebreakers:
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontStyle: 'italic' }}>
                                *Disclaimer: If at any point in the tiebreaker process, one tied team defeated all other tied teams in preliminary play, that team is seeded first. If one tied team lost to all other tied teams, that team is seeded last.
                            </Typography>

                            <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>
                                Two Teams Tied:
                            </Typography>
                            <Box component="ol" sx={{ pl: 3, mb: 2, mt: 0.5 }}>
                                <li><Typography variant="body2"><strong>Head-to-Head:</strong> Winner of head-to-head match advances.</Typography></li>
                                <li><Typography variant="body2"><strong>Goal Quotient:</strong> <code>Goals For / (Goals For + Goals Against)</code> (higher advances).</Typography></li>
                                <li><Typography variant="body2"><strong>Fewest Goals Against.</strong></Typography></li>
                                <li><Typography variant="body2"><strong>Fastest Goal</strong> in preliminary play.</Typography></li>
                                <li><Typography variant="body2"><strong>Coin Flip.</strong></Typography></li>
                            </Box>

                            <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>
                                Three or More Teams Tied:
                            </Typography>
                            <Box component="ol" sx={{ pl: 3, mb: 2, mt: 0.5 }}>
                                <li>
                                    <Typography variant="body2">
                                        <strong>Goal Quotient:</strong> <code>Goals For / (Goals For + Goals Against)</code> (Head-to-head applies if two teams share the same quotient).
                                    </Typography>
                                </li>
                                <li><Typography variant="body2"><strong>Fewest Goals Against.</strong></Typography></li>
                                <li><Typography variant="body2"><strong>Fastest Goal</strong> in preliminary play.</Typography></li>
                                <li><Typography variant="body2"><strong>Coin Flip.</strong></Typography></li>
                            </Box>

                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                ⏱️ Game Lengths & Rules (12U Division):
                            </Typography>
                            <Box component="ul" sx={{ pl: 3, mb: 0, mt: 0.5 }}>
                                <li><Typography variant="body2">Three 14-minute stop-time periods.</Typography></li>
                                <li><Typography variant="body2">Penalties: Minor 1:30, Major 4:00, Misconduct 8:00.</Typography></li>
                                <li><Typography variant="body2">Running clock: 7-goal lead in 2nd period or 5-goal lead in 3rd period.</Typography></li>
                            </Box>
                        </AccordionDetails>
                    </Accordion>

                    {/* Facilities & Rink Locations Accordion */}
                    <Accordion variant="outlined" sx={{ mb: 4, borderRadius: 2, '&:before': { display: 'none' } }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <LocationOnIcon color="primary" />
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Tournament Facilities & Rink Addresses
                                </Typography>
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                            <Divider sx={{ mb: 2 }} />
                            <Stack spacing={2}>
                                <Box>
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        Griff&apos;s Icehouse at Belknap Park
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        30 Coldbrook St NE, Grand Rapids, MI 49503 • Features 2 sheets: East Rink &amp; West Rink
                                    </Typography>
                                    <Stack direction="row" spacing={2} sx={{ mt: 0.5 }} flexWrap="wrap">
                                        <Link href="https://maps.app.goo.gl/BQfdNDLZ9TxpUuTz9" target="_blank" rel="noopener noreferrer" variant="body2">
                                            Google Maps Directions
                                        </Link>
                                        <Link href="https://watch.livebarn.com/en/video/772" target="_blank" rel="noopener noreferrer" variant="body2">
                                            LiveBarn (East Rink)
                                        </Link>
                                        <Link href="https://watch.livebarn.com/en/video/771" target="_blank" rel="noopener noreferrer" variant="body2">
                                            LiveBarn (West Rink)
                                        </Link>
                                    </Stack>
                                </Box>
                                <Divider />
                                <Box>
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        Eagles Ice Center
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        2600 Village Dr SE, Grand Rapids, MI 49506
                                    </Typography>
                                    <Stack direction="row" spacing={2} sx={{ mt: 0.5 }} flexWrap="wrap">
                                        <Link href="https://goo.gl/maps/QnxRQTYpUVwKzjub7" target="_blank" rel="noopener noreferrer" variant="body2">
                                            Google Maps Directions
                                        </Link>
                                        <Link href="https://watch.livebarn.com/en/video/1007" target="_blank" rel="noopener noreferrer" variant="body2">
                                            LiveBarn
                                        </Link>
                                    </Stack>
                                </Box>
                                <Divider />
                                <Box>
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        Kentwood Ice Arena
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        6230 Kalamazoo Ave SE, Grand Rapids, MI 49508
                                    </Typography>
                                    <Link href="https://goo.gl/maps/gYjWNyzMJxutQx6KA" target="_blank" rel="noopener noreferrer" variant="body2">
                                        Google Maps Directions
                                    </Link>
                                </Box>
                                <Divider />
                                <Box>
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        Griff&apos;s Georgetown Ice Arena
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        8500 48th Ave, Hudsonville, MI 49426
                                    </Typography>
                                    <Link href="https://goo.gl/maps/75Pa7iMFUHWJfyxu6" target="_blank" rel="noopener noreferrer" variant="body2">
                                        Google Maps Directions
                                    </Link>
                                </Box>
                                <Divider />
                                <Box>
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        Patterson Ice Center
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        2550 Patterson Ave SE, Grand Rapids, MI 49546
                                    </Typography>
                                    <Link href="https://goo.gl/maps/Qc9eEDFdjE3sREgTA" target="_blank" rel="noopener noreferrer" variant="body2">
                                        Google Maps Directions
                                    </Link>
                                </Box>
                                <Divider />
                                <Box>
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        Walker Ice and Fitness
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        4151 Remembrance Rd NW, Grand Rapids, MI 49534
                                    </Typography>
                                    <Link href="https://goo.gl/maps/hP8F1X53r3jPvS5NA" target="_blank" rel="noopener noreferrer" variant="body2">
                                        Google Maps Directions
                                    </Link>
                                </Box>
                            </Stack>
                        </AccordionDetails>
                    </Accordion>
                </>
            )}

            {/* Direct Tournament Links Table */}
            <LinksTable title="Key Tournament Links" links={howiesLinks2026} />
        </>
    );
};

export default HowiesHometownCup2026Page;
